import { NavLink, useNavigate } from "react-router-dom";
import { useState, type ReactNode } from "react";
import { logout, type AuthUser } from "../lib/api";
import { initials } from "../lib/format";
import { cls } from "../lib/format";

const NAV = [
  { label: "Dashboard", path: "/dashboard", icon: "▦" },
  {
    label: "Inbox Placement",
    icon: "✉",
    children: [
      { label: "Tests", path: "/tests" },
      { label: "Automated Monitoring", path: "/monitoring" },
    ],
  },
  {
    label: "Accounts",
    icon: "☰",
    children: [
      { label: "Seed Accounts", path: "/accounts/seeds" },
      { label: "Sending Accounts", path: "/accounts/sending" },
    ],
  },
  { label: "Email Templates", path: "/templates", icon: "❏" },
  { label: "Reports", path: "/reports", icon: "◫" },
  { label: "Alerts", path: "/alerts", icon: "!" },
  {
    label: "Admin",
    icon: "⚙",
    children: [
      { label: "Seed Groups", path: "/admin/seed-groups" },
      { label: "Providers", path: "/admin/providers" },
      { label: "System Health", path: "/admin/system-health" },
      { label: "Logs", path: "/admin/logs" },
      { label: "Settings", path: "/admin/settings" },
    ],
  },
];

export function Layout({ user, children }: { user: AuthUser; children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen">
      <aside
        className={cls(
          "fixed inset-y-0 left-0 z-40 w-64 transform border-r border-ink-200 bg-white transition-transform lg:static lg:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-14 items-center gap-2 border-b border-ink-100 px-5">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-600 text-sm font-bold text-white">IP</div>
          <div>
            <div className="text-sm font-bold text-ink-900">Inbox Placement</div>
            <div className="text-[10px] text-ink-400">Deliverability Platform</div>
          </div>
        </div>
        <nav className="space-y-0.5 overflow-y-auto p-3">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label}>
                <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink-400">
                  {item.icon} {item.label}
                </div>
                {item.children.map((child) => (
                  <NavLink
                    key={child.path}
                    to={child.path}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      cls(
                        "block rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                        isActive ? "bg-brand-50 text-brand-700" : "text-ink-600 hover:bg-ink-50 hover:text-ink-900",
                      )
                    }
                  >
                    {child.label}
                  </NavLink>
                ))}
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path!}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cls(
                    "block rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    isActive ? "bg-brand-50 text-brand-700" : "text-ink-600 hover:bg-ink-50 hover:text-ink-900",
                  )
                }
              >
                {item.icon} {item.label}
              </NavLink>
            ),
          )}
        </nav>
        <div className="border-t border-ink-100 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">
              {initials(user.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-semibold text-ink-800">{user.name}</div>
              <div className="truncate text-[11px] text-ink-400">{user.email}</div>
            </div>
            <button
              title="Sign out"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="text-xs text-ink-400 hover:text-red-600"
            >
              ⎋
            </button>
          </div>
        </div>
      </aside>
      {mobileOpen && <div className="fixed inset-0 z-30 bg-ink-900/30 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-ink-200 bg-white/90 px-4 backdrop-blur lg:px-8">
          <button className="text-ink-500 lg:hidden" onClick={() => setMobileOpen(true)}>
            ☰
          </button>
          <div className="text-sm text-ink-500">
            Email Deliverability Testing <span className="mx-1 text-ink-300">/</span> Workspace
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-ink-400 sm:block">{new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
          </div>
        </header>
        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
